import React, { useContext, useEffect, useMemo } from 'react';
import { ConfigContext } from '../../../../../Contexts/ConfigContext';
import ErrorBoundary from '../../../../../Components/CustomErrorBoundary/CustomErrorBoundary';
import constants from '../constants.json';

//mui imports
import { Grid, Paper, Divider, Box, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import Alert from '@mui/material/Alert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import useCommonStyles from '../../../../../commonStyles';
import PageHeading from '../../../../../Components/PageHeading';
import AddCircleOutlineOutlined from '@mui/icons-material/AddCircleOutlineOutlined';
import CheckBoxInput from '../../../../../Components/CheckBoxInput';
import DropDownSingle from '../../../../../Components/DropDownSingle';

import { ENGINE_TYPE_REGISTRY, SUPPORTED_ENGINE_TYPES } from './registry';
import InputConfig from '../../../../../Components/InputConfig';
import { toast } from 'react-toastify';
import { CONFIG_KEYDATA_CONSTANTS } from '../../../../../core/constants';
import FloorAutomationInfoMessage from './FloorAutomationInfoMessage';

const { GAM_UPR_CONFIG_KEYDATA } = CONFIG_KEYDATA_CONSTANTS;

const FpEngineConfig = ({ keyData, featureDescription }) => {
	const { rawConfig, updateRawConfig } = useContext(ConfigContext);
	const commonStyleClasses = useCommonStyles();
	const { fpDefaultConfig, fpEngineTypes } = constants;
	const enabledKey = `${keyData}.enabled`;
	const fpeEngineConfigKey = `${keyData}.configs`;

	const gamFloorAutomationEnabled =
		!!rawConfig[GAM_UPR_CONFIG_KEYDATA]?.gamUprFloorAutomation?.enabled;

	useEffect(() => {
		const experimentNumberKey = `${keyData}.experimentNumber`;
		const cloneRawConfig = structuredClone(rawConfig);

		if (rawConfig[enabledKey] && !cloneRawConfig[experimentNumberKey]) {
			cloneRawConfig[experimentNumberKey] = 8;
			updateRawConfig(cloneRawConfig);
		}
	}, [rawConfig[enabledKey]]);

	/**
	 * Update a specific field inside a configuration object at a given index.
	 * @param {number} index - Index of the configuration to update.
	 * @param {string} field - Field name to update.
	 * @param {*} value - New value to set.
	 * @returns {void}
	 */
	const handleConfigsChange = (index, field, value) => {
		const cloneRawConfig = structuredClone(rawConfig);
		cloneRawConfig[fpeEngineConfigKey] = cloneRawConfig[fpeEngineConfigKey] || [];
		cloneRawConfig[fpeEngineConfigKey][index] = cloneRawConfig[fpeEngineConfigKey][index] || {};
		// When multiple fields need to be saved atomically (e.g. floorSeries + experimentNumber
		// from the floor series modal), pass them as a single object instead of making two
		// separate calls. Two separate calls both clone the same stale `rawConfig` from the
		// closure — React batches the updates, so the second call overwrites the first and the
		// first field's change is silently lost.
		//
		// Object form usage example:
		//   handleConfigsChange(0, { floorSeries: ['0.5', '1.0'], experimentNumber: 'floor_series_cust' })
		//
		// Single-field usage (existing behaviour, unchanged):
		//   handleConfigsChange(0, 'fillThreshold', 5)
		if (typeof field === 'object' && field !== null) {
			Object.assign(cloneRawConfig[fpeEngineConfigKey][index], field);
		} else {
			cloneRawConfig[fpeEngineConfigKey][index][field] = value;
		}
		updateRawConfig(cloneRawConfig);
	};

	/**
	 * Updates the FPE Engine toggle pair (`floorDecreaserAndLooperEnabled` and `incDecEnabled`)
	 * for a specific configuration entry. These two toggles are mutually exclusive, meaning
	 * if one is set to `true`, the other is automatically forced to `false`.
	 *
	 * @param {number} index - Index of the configuration item to update.
	 * @param {string} field - The field being updated ("floorDecreaserAndLooperEnabled" | "incDecEnabled").
	 * @param {boolean} value - New boolean value to assign to the specified field.
	 * @returns {void}
	 */
	const updateFpeEngineTogglePair = (index, field, value) => {
		const cloneRawConfig = structuredClone(rawConfig);
		cloneRawConfig[fpeEngineConfigKey] = cloneRawConfig[fpeEngineConfigKey] || [];

		const current = cloneRawConfig[fpeEngineConfigKey][index] || {};

		const isLooperField = field === 'floorDecreaserAndLooperEnabled';
		const isIncDecField = field === 'incDecEnabled';

		let nextLooperValue = isLooperField ? value : current.floorDecreaserAndLooperEnabled;
		let nextIncDecValue = isIncDecField ? value : current.incDecEnabled;

		if (value === true) {
			if (isLooperField) nextIncDecValue = false;
			if (isIncDecField) nextLooperValue = false;
		}

		const updatedConfig = {
			...current,
			floorDecreaserAndLooperEnabled: nextLooperValue,
			incDecEnabled: nextIncDecValue
		};

		cloneRawConfig[fpeEngineConfigKey][index] = updatedConfig;
		updateRawConfig(cloneRawConfig);
	};
	/**
	 * Update engine model configuration with defaults based on engine type.
	 * @param {number} index - Index of the configuration to update.
	 * @param {string} selectedEngineType - Selected engine type key.
	 * @returns {void}
	 */
	const handleUpdateModel = (index, selectedEngineType) => {
		if (!ENGINE_TYPE_REGISTRY[selectedEngineType]) {
			toast.error(`Moderator type ${selectedEngineType} is not supported.`);
			return;
		}

		const cloneRawConfig = structuredClone(rawConfig);
		cloneRawConfig[fpeEngineConfigKey] = cloneRawConfig[fpeEngineConfigKey] || [];
		cloneRawConfig[fpeEngineConfigKey][index] = {
			...ENGINE_TYPE_REGISTRY[selectedEngineType].getDefault(fpDefaultConfig),
			engineType: selectedEngineType
		};

		updateRawConfig(cloneRawConfig);
	};

	/**
	 * Add a new engine configuration with default values.
	 * @param {string} [engineType=SUPPORTED_ENGINE_TYPES[0]] - Engine type to initialize.
	 * @returns {void}
	 */
	const addNewConfig = (engineType = SUPPORTED_ENGINE_TYPES[0]) => {
		const cloneRawConfig = structuredClone(rawConfig);
		cloneRawConfig[fpeEngineConfigKey] = cloneRawConfig[fpeEngineConfigKey] || [];
		const defaultEngineConfig = ENGINE_TYPE_REGISTRY[engineType].getDefault(fpDefaultConfig);
		cloneRawConfig[fpeEngineConfigKey].push(defaultEngineConfig);
		updateRawConfig(cloneRawConfig);
	};

	/**
	 * Delete a configuration at a given index.
	 * Ensures at least one configuration always exists.
	 * @param {number} index - Index of the configuration to delete.
	 * @returns {void}
	 */
	const deleteConfig = (index) => {
		const cloneRawConfig = structuredClone(rawConfig);
		cloneRawConfig[fpeEngineConfigKey] = cloneRawConfig[fpeEngineConfigKey] || [];
		if (cloneRawConfig[fpeEngineConfigKey].length < 1) {
			toast.error('At least one configuration must be present.');
			return;
		}

		cloneRawConfig[fpeEngineConfigKey].splice(index, 1);
		updateRawConfig(cloneRawConfig);
	};

	const isFpeEngineConfigExist = useMemo(() => {
		return !!(rawConfig[fpeEngineConfigKey] && rawConfig[fpeEngineConfigKey].length > 0);
	}, [rawConfig[fpeEngineConfigKey], keyData]);

	return (
		<Paper className={commonStyleClasses.mainPagePaperMarginStyle}>
			<PageHeading
				headingName="Floor Price Moderator"
				headingPath={['Dashboard', 'Framerate', 'Network Data', 'Floor Price Moderator']}
				description="Manage Floor Price Moderator Configurations"
				showInfoButton={true}
				infoMessage={featureDescription}
			/>

			{gamFloorAutomationEnabled ? (
				<FloorAutomationInfoMessage />
			) : (
				<Box sx={{ width: '90%', margin: '2rem auto', padding: '2rem' }}>
					<ErrorBoundary>
						<Grid container spacing={3}>
							<Grid item xs={12}>
								<CheckBoxInput
									keyData={enabledKey}
									label="Enabled"
									object={rawConfig}
									updateObject={updateRawConfig}
									containerStyles={{
										width: 'fit-content'
									}}
								/>
							</Grid>

							<Grid container spacing={4} mt={1}>
								{isFpeEngineConfigExist &&
									rawConfig[fpeEngineConfigKey].map((engineModelConfig, index) => {
										const FeEngineModel = engineModelConfig?.engineType
											? ENGINE_TYPE_REGISTRY[engineModelConfig.engineType]?.component
											: null;
										return (
											<Grid item xs={6} sm={6} key={index}>
												<Paper elevation={3} sx={{ padding: 3, borderRadius: 2, height: '100%' }}>
													<Box
														sx={{
															display: 'flex',
															justifyContent: 'space-between',
															alignItems: 'center',
															mb: 2
														}}
													>
														<Typography variant="h6" fontWeight="bold">
															FP Moderator Configuration - {index + 1}
														</Typography>

														<DeleteIcon
															color="error"
															fontSize="large"
															onClick={() => deleteConfig(index)}
															sx={{ cursor: 'pointer' }}
														/>
													</Box>

													<Grid container spacing={2} mt={1}>
														<Grid item xs={12} sm={12}>
															<DropDownSingle
																keyData="engineType"
																label="Moderator Type"
																containerStyles={{ marginLeft: '0.4rem' }}
																labelStyles={{ fontSize: '1.7rem' }}
																selectInputStyles={{ height: '5rem' }}
																isObjectEditor={false}
																directValue={engineModelConfig?.engineType}
																list={fpEngineTypes}
																disabled={!rawConfig[enabledKey]}
																updateDirectValue={(selectedEngineType) =>
																	handleUpdateModel(index, selectedEngineType)
																}
															/>
														</Grid>

														{FeEngineModel ? (
															<FeEngineModel
																engineModelConfig={engineModelConfig}
																index={index}
																handleConfigsChange={handleConfigsChange}
																updateSwitchInputField={updateFpeEngineTogglePair}
																keyData={keyData}
																enabled={!!rawConfig[enabledKey]}
															/>
														) : (
															<Typography color="primary" mt={4} ml={3}>
																Engine type <b>{engineModelConfig?.engineType}</b> is not supported
																in this UI.
															</Typography>
														)}
														<Grid item xs={6} sm={6}>
															<InputConfig
																keyData={`${keyData}.configs.${index}.splitPercentage`}
																label="Split Percentage"
																inputType="number"
																isObjectEditor={false}
																labelStyles={{ fontSize: '1.5rem' }}
																containerStyles={{ marginTop: '1rem', marginLeft: '0.4rem' }}
																directValue={engineModelConfig.splitPercentage}
																editable={!!rawConfig[enabledKey]}
																updateDirectValue={(value) =>
																	handleConfigsChange(index, 'splitPercentage', parseInt(value, 10))
																}
															/>
														</Grid>
													</Grid>
												</Paper>
											</Grid>
										);
									})}
							</Grid>

							<Grid item xs={12}>
								<AddCircleOutlineOutlined
									className={commonStyleClasses.icon}
									color="primary"
									fontSize="medium"
									style={{ margin: '3rem auto', display: 'flex' }}
									onClick={() => addNewConfig()}
								/>
							</Grid>
						</Grid>
					</ErrorBoundary>
				</Box>
			)}
		</Paper>
	);
};

export default FpEngineConfig;
